/**
 * Collection selection model (CB-4) — the policy layer between a collection
 * surface's gestures and a SelectionStore.
 *
 * Written by READING project-history-modal.js (My Projects keeps its own
 * `selectedIds: Set` until CB-9), it generalizes the behaviours that are
 * hardcoded there:
 *   · `canSelect` replaces the four inline `role === 'member'` exclusions and
 *     is enforced HERE, so a range, a select-all and a programmatic
 *     setSelection all obey it — not just the checkbox path;
 *   · select-all follows the ACTIVE FILTER (`getOrder()`), and never touches
 *     a record outside it: checking adds the selectable visible ids, and
 *     unchecking removes only the visible ones, so a selection made under a
 *     previous filter survives exactly as `summarize()` discloses it;
 *   · a Shift-range does NOT call `store.extendTo`. The store cannot know
 *     `canSelect`, so a range spanning an unselectable row would select it and
 *     then 403 — and calling `extendTo` then `remove()` costs two commits and
 *     two notifications, flickering the count. The model computes the range,
 *     filters it, and issues ONE `set()` with the anchor untouched;
 *   · `getOrder()` is resolved at press time, never captured — palette's
 *     lesson: a list captured at build time ranges over a card the search has
 *     hidden.
 *
 * Imports only Tier 1 + core/selection-store.js + ui/section-list-interaction.js
 * (all shell-free), so it runs under plain Node.
 */

import { SelectionStore } from '../core/selection-store.js';
import {
  resolveClickIntent,
  resolveRowKeyIntent,
  extendTargetFromLead,
  summarizeSelection,
  selectionAnnouncement
} from './section-list-interaction.js';

/**
 * @param {{
 *   store?: SelectionStore,
 *   getOrder: () => string[],        — visible candidate ids, document order, read per call
 *   getAllIds?: () => string[],      — every live id (visible + filtered); defaults to getOrder
 *   canSelect?: (id: string) => boolean,
 *   noun?: string,
 *   nounPlural?: string
 * }} options
 */
export function createCollectionSelection({
  store = new SelectionStore(),
  getOrder,
  getAllIds,
  canSelect,
  noun = 'item',
  nounPlural
} = {}) {
  if (typeof getOrder !== 'function') {
    throw new Error('createCollectionSelection requires getOrder()');
  }
  const allIds = typeof getAllIds === 'function' ? getAllIds : getOrder;
  const selectable = typeof canSelect === 'function' ? canSelect : () => true;

  /** The anchor→id range over the current visible order, unselectables dropped. */
  function rangeIds(id, order) {
    const anchor = store.getAnchor();
    if (!anchor) return null;
    const a = order.indexOf(anchor);
    const b = order.indexOf(id);
    if (a === -1 || b === -1) return null;
    const [lo, hi] = a <= b ? [a, b] : [b, a];
    return order.slice(lo, hi + 1).filter(selectable);
  }

  /** One commit, anchor untouched — the extendTo deviation the header explains. */
  function applyRange(id, { additive = false } = {}) {
    const order = getOrder();
    const range = rangeIds(id, order);
    if (range === null) return applyIntent(id, 'replace');
    const anchor = store.getAnchor();
    const next = additive ? [...store.getIds(), ...range] : range;
    return store.set(next, { order, anchor, lead: id, source: 'collection-range' });
  }

  /**
   * Apply a resolved intent to `id`. Unselectable ids are a no-op for every
   * intent — the caller paints them undisclosed-unselectable (disabled
   * checkbox), it does not route their clicks here expecting a selection.
   * @param {string} id
   * @param {'replace'|'toggle'|'range'|'range-add'} intent
   * @returns {boolean} whether the store changed
   */
  function applyIntent(id, intent) {
    if (!id) return false;
    if (intent === 'range' || intent === 'range-add') {
      return applyRange(id, { additive: intent === 'range-add' });
    }
    if (!selectable(id)) return false;
    const order = getOrder();
    if (intent === 'toggle') return store.toggle(id, { order, source: 'collection-toggle' });
    return store.select(id, { order, source: 'collection-replace' });
  }

  return {
    store,

    // ── gestures ───────────────────────────────────────────────────────────
    /** Pointer gesture on an item: modifiers → intent → one store write. */
    handleItemClick(id, event) {
      return applyIntent(id, resolveClickIntent(event));
    },
    applyIntent,

    /**
     * Row-level keydown. Shift+Arrow extends FROM THE LEAD (never from focus)
     * as one range commit; plain arrows are the caller's focus machinery and
     * return the resolved intent untouched so it can act.
     * @param {KeyboardEvent|object} event
     * @param {string|null} focusedId — fallback start for a fresh extension
     * @returns {{type: string}|null} the resolved intent (null = not ours)
     */
    handleRowKey(event, focusedId = null) {
      const intent = resolveRowKeyIntent(event);
      if (!intent) return null;
      if (intent.type === 'extend') {
        const target = extendTargetFromLead(store.getLead(), getOrder(), intent.dir, focusedId);
        if (target) applyRange(target, { additive: false });
        return intent;
      }
      if (intent.type === 'toggle-focused') {
        if (focusedId) applyIntent(focusedId, 'toggle');
        return intent;
      }
      return intent; // 'focus-move' — the caller moves focus
    },

    // ── select-all ─────────────────────────────────────────────────────────
    /** Filter-scoped: touches ONLY the selectable ids currently visible. */
    selectAllVisible(checked) {
      const visible = getOrder().filter(selectable);
      if (visible.length === 0) return false;
      if (checked)
        return store.add(visible, { order: getOrder(), source: 'collection-select-all' });
      return store.remove(visible, { source: 'collection-select-all' });
    },

    /** The tri-state the select-all checkbox paints, derived once. */
    selectAllState() {
      const visible = getOrder().filter(selectable);
      const selected = store.getSet();
      const selectedVisible = visible.filter((id) => selected.has(id)).length;
      return {
        checked: visible.length > 0 && selectedVisible === visible.length,
        indeterminate: selectedVisible > 0 && selectedVisible < visible.length,
        disabled: visible.length === 0
      };
    },

    // ── programmatic ───────────────────────────────────────────────────────
    /**
     * Whole-set write (toolbarActions — "All Pages", "Homepage Only"). Obeys
     * `canSelect` and drops ids the surface no longer knows; may include ids
     * the current filter hides, which `summarize()` then discloses.
     */
    setSelection(ids) {
      const known = new Set(allIds());
      const next = (Array.isArray(ids) ? ids : []).filter((id) => known.has(id) && selectable(id));
      return store.set(next, { order: allIds(), source: 'collection-set' });
    },

    clear() {
      return store.clear({ source: 'collection-clear' });
    },

    /** Drop ids that left the data set; call before every repaint. */
    prune() {
      return store.prune(allIds(), { source: 'collection-prune' });
    },

    // ── reads ──────────────────────────────────────────────────────────────
    getSelectedIds: () => store.getIds(),
    isSelected: (id) => store.isSelected(id),
    size: () => store.size(),

    /** {count, hiddenCount, text} against the CURRENT visible order. */
    summarize() {
      return summarizeSelection(store.getIds(), getOrder());
    },

    /** The live-region sentence for the current state. */
    announcement() {
      const { count, hiddenCount } = this.summarize();
      return selectionAnnouncement({ size: count, hiddenCount, noun, nounPlural });
    },

    subscribe: (fn) => store.subscribe(fn)
  };
}
